import React from "react";
import {
  FaRegEye,
  FaBullseye,
  FaAward,
  FaUsers,
  FaGraduationCap,
  FaRocket,
} from "react-icons/fa";
import aboutLogo from "../../assets/images/about.png";
import { Card, Container, IconTile, SectionHead } from "./ui";

const stats = [
  { icon: FaUsers, number: "4M+", label: "Students Trained" },
  { icon: FaGraduationCap, number: "1200+", label: "Campus Partners" },
  { icon: FaAward, number: "24+", label: "Years Experience" },
  { icon: FaRocket, number: "100%", label: "Placement Support" },
];

const features = [
  {
    icon: FaRegEye,
    title: "Vision",
    description:
      "Bridging the gap between potential and performance through transformative learning experiences.",
  },
  {
    icon: FaBullseye,
    title: "Mission",
    description:
      "Our mission is to provide high-quality, customized training programs that empower individuals and organizations to achieve their goals and drive success.",
  },
];

const whyChoose = [
  {
    icon: FaGraduationCap,
    title: "Expert Faculty",
    description:
      "Learn from industry experts with years of experience in placement training and career development.",
  },
  {
    icon: FaRocket,
    title: "Proven Results",
    description:
      "Our track record speaks for itself with millions of successful placements across top companies.",
  },
  {
    icon: FaUsers,
    title: "Comprehensive Support",
    description:
      "From training to placement, we provide end-to-end support for your career journey.",
  },
];

const AboutPage = ({ aboutComponentRef }) => (
  <section
    ref={aboutComponentRef}
    id="about"
    data-section="about"
    className="scroll-mt-24 bg-white py-20"
  >
    <Container>
      <SectionHead
        eyebrow="About SMART"
        title="Discover SMART solutions"
        subtitle="For your educational and career success!"
      />

      <div className="grid items-center gap-12 lg:grid-cols-2">
        {/* Circular portrait with floating badge — same ring treatment as the hero */}
        <div className="relative mx-auto w-full max-w-md lg:max-w-lg">
          <div className="relative aspect-square">
            {/* Outer ring */}
            <div className="absolute inset-0 rounded-full border border-dashed border-brand-300" />
            {/* Tinted disc */}
            <div className="absolute inset-[6%] rounded-full bg-brand-50" />

            <div className="absolute inset-[10%] overflow-hidden rounded-full">
              <img
                src={aboutLogo}
                alt="SMART Training Team"
                className="h-full w-full object-cover object-center"
              />
            </div>

            {/* Floating shapes */}
            <span className="absolute right-[4%] top-[16%] h-5 w-5 rounded-full bg-brand" />
            <span className="absolute bottom-[12%] left-[2%] h-8 w-8 rounded-full bg-brand" />
            <span className="absolute right-[-2%] top-[52%] h-6 w-6 rounded-full bg-baseYellow" />
          </div>

          <Card className="absolute bottom-[2%] right-0 flex items-center gap-3 p-4 shadow-lg sm:right-[4%]">
            <IconTile icon={FaAward} />
            <div>
              <div className="text-xl font-bold text-ink">24+</div>
              <div className="text-xs text-darkgrey">Years of Excellence</div>
            </div>
          </Card>
        </div>

        {/* Copy */}
        <div>
          <h3 className="text-2xl font-bold text-ink">
            Pioneering Excellence Since 2002
          </h3>
          <div className="mt-4 space-y-4 leading-relaxed text-darkgrey">
            <p className="text-sm">
              SMART is the Pioneer in Placement Training and the Single Largest
              Employability Enhancer in India, established by our honourable
              Founder and Managing Director, Mr. RAM RV in the year 2002.
            </p>
            <p className="text-sm">
              Over these 24 years, SMART has trained over 4 million students in
              1200+ campuses PAN India including the IITs, IIMs and leading
              technical and non-technical institutions for students from the
              first year onwards.
            </p>
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {features.map((feature) => (
              <Card key={feature.title} className="p-5">
                <IconTile icon={feature.icon} className="mb-4" />
                <h4 className="mb-2 text-base font-bold text-ink">
                  {feature.title}
                </h4>
                <p className="text-sm leading-relaxed text-darkgrey">
                  {feature.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Impact figures */}
      <div className="mt-24 rounded-3xl bg-brand p-8 sm:p-12">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <h3 className="text-2xl font-bold text-white sm:text-3xl">
            Our impact in numbers
          </h3>
          <p className="mt-3 text-white/80">
            Transforming careers, one student at a time
          </p>
        </div>

        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-2xl bg-white/10 p-5 text-center"
            >
              <stat.icon className="mx-auto mb-3 h-6 w-6 text-white" />
              <div className="text-2xl font-bold text-white sm:text-3xl">
                {stat.number}
              </div>
              <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-white/70">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Why choose SMART */}
      <div className="mt-20">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <h3 className="text-2xl font-bold text-ink sm:text-3xl">
            Why choose SMART?
          </h3>
          <p className="mt-3 text-darkgrey">
            We combine decades of experience with innovative teaching methods to
            deliver exceptional results
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {whyChoose.map((item) => (
            <Card key={item.title} className="p-6">
              <IconTile icon={item.icon} className="mb-4" />
              <h4 className="mb-2 text-base font-bold text-ink">
                {item.title}
              </h4>
              <p className="text-sm leading-relaxed text-darkgrey">
                {item.description}
              </p>
            </Card>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

export default AboutPage;
